<template>
  <popup-dialog
    :visible="visible"
    class="popup-duplicatefolder"
    :close-button-visible="true"
    :title="$t('labels.newFolder')"
    @close="close"
  >
    <form class="popup-duplicatefolder__form" @submit.prevent="copy()">
      <input-dropdown
        :value="
          state.workspaceId == null ? null : workspaces[state.workspaceId]
        "
        :options="workspaces"
        prop="name"
        :label="$t('words.workspace', 1)"
        :searchable="true"
        group-key="organization"
        :error="state.fieldErrors?.workspace_id"
        @update:value="state.workspaceId = $event.id"
      />

      <input-text
        v-model:value="state.newName"
        :autofocus="true"
        :label="$t('labels.name')"
        :error="state.fieldErrors?.name"
        @update:value="state.fieldErrors.name = undefined"
      />

      <button
        class="button popup-duplicatefolder__button mt-4"
        type="submit"
        :disabled="state.copying"
      >
        <template v-if="state.copying">
          {{ $t("messages.saving") }}
        </template>
        <template v-else>
          {{ $t("labels.duplicate") }}
        </template>
      </button>
    </form>
  </popup-dialog>
</template>

<script setup lang="ts">
import { watch, reactive } from "vue";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputText from "@core/input/InputText.vue";
import InputDropdown from "@core/input/InputDropdown.vue";

import { useWorkspaces } from "@/api/workspaces.store";
import { showError, showMessage } from "@core/ui/alert/alert";
import { getRouteToFolder } from "@core/routes/routeDetector";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { BadRequestError } from "@core/errors/Errors";

import type { WorkspaceFolder } from "@/api/Workspace";

const props = withDefaults(
  defineProps<{
    visible?: boolean;
    folder?: { id: number; workspace: number; name: string };
  }>(),
  {
    visible: false,
    folder: undefined
  }
);
const emit = defineEmits<{
  (event: "close"): void;
  (event: "duplicated", folder: WorkspaceFolder): void;
}>();

const { userWorkspace, workspaces, copyFolder } = useWorkspaces();
const { t } = useI18n();
const router = useRouter();

const state = reactive({
  workspaceId: undefined as number | undefined,
  folderSlug: undefined as string | undefined,
  newName: "",
  fieldErrors: {} as Record<string, string | undefined>,

  copying: false
});

watch(
  () => props.folder?.id,
  () => {
    state.workspaceId = props.folder?.workspace ?? userWorkspace.value?.id;
    state.newName = `${props.folder?.name}${t("labels.copySuffix")}`;
  },
  { immediate: true }
);

async function copy() {
  if (state.workspaceId == null) {
    showMessage(t("messages.chooseWorkspace"));
    return;
  }

  if (props.folder == null) {
    return;
  }

  state.copying = true;
  try {
    const data = {
      workspaceId: state.workspaceId,
      name: state.newName
    };
    const newFolder = await copyFolder(props.folder, data);

    router.push(getRouteToFolder(newFolder));
    close();
    emit("duplicated", newFolder);
  } catch (_error) {
    const error = _error as BadRequestError;
    state.fieldErrors = error.fields;
    showError(error);
  } finally {
    state.copying = false;
  }
}

function close() {
  emit("close");
}
</script>

<style lang="scss">
.popup-duplicatefolder {
  overflow: visible;

  &__form {
    padding: 2rem;
    display: flex;
    gap: 0.5rem;
    flex-direction: column;
  }

  &__button {
    align-self: center;
  }
}
</style>
